'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/transformers'

const SelfAttentionStepper = dynamic(() => import('../_components/widgets/TransformerWidgets').then(m => m.SelfAttentionStepper), { ssr: false })

export default function SelfAttention() {
  return (
    <Lesson>
      <Step>
        <H>All tokens at once</H>
        <P>
          Attention for one query is a soft lookup. <strong>Self-attention</strong> does it for every token in the sequence at
          the same time, with matrices. Stack the token embeddings as the rows of <M>X</M>:
        </P>
        <MB>{'Q = XW_Q \\qquad K = XW_K \\qquad V = XW_V'}</MB>
        <MB>{'\\text{Attention}(Q, K, V) = \\text{softmax}\\!\\left(\\frac{QK^{\\top}}{\\sqrt{d}}\\right) V'}</MB>
        <P>
          This single line is the heart of every modern language model. <M>{'QK^{\\top}'}</M> computes every query-key dot
          product in one matrix multiply; softmax (row by row) makes the weights; multiplying by <M>V</M> blends the values.
        </P>
        <Callout kind="notation">
          <M>{'K^{\\top}'}</M> is K <strong>transposed</strong> (rows ↔ columns), so that row i of Q gets dotted with row j of K.
          The <M>{'\\sqrt{d}'}</M> (d = size of each query) keeps the scores from growing with dimension, so softmax doesn’t
          become too peaky.
        </Callout>
      </Step>

      <Step gate="q-mask">
        <H>Every matrix, visible</H>
        <Figure n="9.3" wide caption="Self-attention on four tokens, computed for real. Step through the stages, and follow one token to see exactly which others it draws from.">
          <SelfAttentionStepper />
        </Figure>
        <P>
          Notice the <strong>causal mask</strong>. A language model is trained to predict the next token, so each position must
          not see the tokens after it — that would be cheating. Setting those scores to <M>{'-\\infty'}</M> before softmax gives
          them exactly zero weight.
        </P>
        <Quiz
          id="q-mask"
          question="Without the causal mask, what would go wrong when training a next-token predictor?"
          options={[
            { label: 'Each position could just look at the next token and copy it — perfect training loss, useless model.', correct: true },
            { label: 'Training would be slower.', why: 'Speed isn’t the issue — the task becomes trivially solvable by peeking.' },
            { label: 'Softmax wouldn’t work.', why: 'Softmax works fine either way. The problem is information leaking from the future.' },
          ]}
        />
      </Step>

      <Step gate="self-attention">
        <H>Implement self-attention</H>
        <Exercise
          id="self-attention"
          title="Self-attention with arrays"
          {...EX.selfAttention}
          hints={[
            <>
              <C>const Q = mat.mul(X, Wq)</C>, likewise K and V. Scores: <C>mat.mul(Q, mat.transpose(K))</C>.
            </>,
            <>
              Map each score: <C>{'causal && j > i ? -Infinity : s / Math.sqrt(d)'}</C>. Then softmax each row and{' '}
              <C>mat.mul(weights, V)</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-heads">
        <H>Many heads</H>
        <P>
          One attention pattern per layer is limiting — a token may need to track its subject, the previous word and an opening
          quote, all at once. So transformers run several attention <strong>heads</strong> in parallel, each with its own{' '}
          <M>{'W_Q, W_K, W_V'}</M> and each producing a smaller output. Their outputs are glued side by side and mixed with one
          more matrix. This is <strong>multi-head attention</strong>.
        </P>
        <Quiz
          id="q-heads"
          question="An embedding of size 768 uses 12 heads. What size is each head’s query?"
          options={[{ label: '64', correct: true }, { label: '768', why: 'The embedding is split across heads so the total work stays the same.' }, { label: '12', why: '12 is the number of heads; 768 ÷ 12 is the size of each.' }]}
          explain="768 ÷ 12 = 64. Those are GPT-2 (small)’s real numbers."
        />
      </Step>
    </Lesson>
  )
}
